import { useEffect, useState } from 'react';
import {
  X, Phone, MapPin, Clock, ShieldAlert, AlertTriangle,
  Bell, Shield, CheckCircle, Loader2, Hash, User,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { AlarmEventSeverity, AlarmSiteExtended, ArcContact } from '@asi/shared';
import { fetchSiteDetail, getCidDescription } from '@/services/arc';
import { supabase } from '@/services/supabase';
import type { ArcEventWithSite } from './EventRibbon';
import { useProcessConfirmSound } from './ArcRealtimeProvider';
import { useSettingsStore, chipCls } from '@/stores/settings-store';

// ─── Constants ────────────────────────────────────────────────

const SEVERITY_CFG: Record<AlarmEventSeverity, {
  color: string; bg: string; border: string; icon: typeof ShieldAlert; label: string;
}> = {
  CRITICAL: { color: 'text-red-600',    bg: 'bg-red-50 dark:bg-red-950/30',    border: 'border-red-300 dark:border-red-800',    icon: ShieldAlert,    label: 'Crítico'     },
  ALARM:    { color: 'text-orange-600', bg: 'bg-orange-50 dark:bg-orange-950/30', border: 'border-orange-300 dark:border-orange-800', icon: AlertTriangle, label: 'Alarma'      },
  WARNING:  { color: 'text-yellow-600', bg: 'bg-yellow-50 dark:bg-yellow-950/30', border: 'border-yellow-300 dark:border-yellow-800', icon: Bell,         label: 'Advertencia' },
  INFO:     { color: 'text-blue-600',   bg: 'bg-blue-50 dark:bg-blue-950/30',   border: 'border-blue-200 dark:border-blue-800',   icon: Shield,         label: 'Información' },
};

const SERVICE_LEVEL_CFG: Record<string, { label: string; cls: string }> = {
  basic:      { label: 'Básico',      cls: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' },
  premium:    { label: 'Premium',     cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' },
  enterprise: { label: 'Empresarial', cls: 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300' },
};

const RELATION_LABELS: Record<string, string> = {
  owner: 'Propietario', keyholder: 'Llavero', manager: 'Gerente',
  security: 'Seguridad', police: 'Policía', fire: 'Bomberos',
  medical: 'Médico', other: 'Otro',
};

const GT_TZ = 'America/Guatemala';

// ─── Helpers ──────────────────────────────────────────────────

function fmtTime(iso: string) {
  return new Date(iso).toLocaleTimeString('es-GT', {
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false, timeZone: GT_TZ,
  });
}

function fmtElapsed(iso: string) {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
}

function formatSeniority(createdAt: string): string {
  const months = (new Date().getFullYear() - new Date(createdAt).getFullYear()) * 12
    + (new Date().getMonth() - new Date(createdAt).getMonth());
  if (months < 1) return 'nuevo';
  if (months < 12) return `${months}m cliente`;
  const y = Math.floor(months / 12), m = months % 12;
  return m > 0 ? `${y}a ${m}m` : `${y}a cliente`;
}

function Skeleton({ className }: { className?: string }) {
  return <div className={cn('animate-pulse bg-muted rounded', className)} />;
}

// ─── Props ────────────────────────────────────────────────────

export interface ProcessEventModalProps {
  event: ArcEventWithSite | null;
  onClose: () => void;
  onProcess: (eventId: string, notes?: string) => Promise<void>;
}

// ─── Main Component ───────────────────────────────────────────

export function ProcessEventModal({ event, onClose, onProcess }: ProcessEventModalProps) {
  const [notes, setNotes] = useState('');
  const [processing, setProcessing] = useState(false);
  const [site, setSite] = useState<AlarmSiteExtended | null>(null);
  const [contacts, setContacts] = useState<ArcContact[]>([]);
  const [loadingSite, setLoadingSite] = useState(false);
  const [accountTier, setAccountTier] = useState<string | null>(null);
  const [accountCreatedAt, setAccountCreatedAt] = useState<string | null>(null);
  const playConfirm = useProcessConfirmSound();
  const quickReplies = useSettingsStore((s) =>
    s.quickReplies.filter((r) => r.modals === 'process' || r.modals === 'both' || r.modals === 'all').sort((a, b) => a.order - b.order)
  );

  // Fetch site + contacts when modal opens
  useEffect(() => {
    if (!event?.site_id) return;
    setLoadingSite(true);
    fetchSiteDetail(event.site_id)
      .then((siteData) => {
        setSite(siteData);
        setContacts(siteData?.contacts ?? []);
        // Look up account tier + seniority from the account linked to this site
        const accountId = (siteData as any)?.account_id;
        if (accountId) {
          supabase.from('accounts').select('client_tier, created_at').eq('id', accountId).maybeSingle()
            .then(({ data }) => {
              setAccountTier(data?.client_tier ?? null);
              setAccountCreatedAt(data?.created_at ?? null);
            }, () => {});
        }
      })
      .finally(() => setLoadingSite(false));

    return () => {
      setSite(null);
      setContacts([]);
      setNotes('');
      setAccountTier(null);
      setAccountCreatedAt(null);
    };
  }, [event?.id, event?.site_id]);

  if (!event) return null;

  const severity = (event.severity ?? 'INFO') as AlarmEventSeverity;
  const cfg = SEVERITY_CFG[severity] ?? SEVERITY_CFG.INFO;
  const SevIcon = cfg.icon;
  const svcCfg = SERVICE_LEVEL_CFG[site?.service_level ?? 'basic'] ?? SERVICE_LEVEL_CFG.basic;

  // Top contacts: merge primary site contact + arc_contacts, max 3
  const allContacts: ArcContact[] = [
    ...(site?.contact_name ? [{
      id: `primary-${site.id}`,
      site_id: site.id,
      name: site.contact_name,
      phone: site.contact_phone ?? null,
      mobile: null,
      email: site.contact_email ?? null,
      relation: 'owner' as const,
      relation_notes: null,
      priority: 0,
      can_verify: true, can_dispatch: true, can_arm_disarm: true,
      notify_alarms: true, notify_arming: true, notify_tamper: true, notify_low_battery: true,
      created_at: '', updated_at: '',
    }] : []),
    ...contacts,
  ].slice(0, 3);

  async function handleProcess() {
    setProcessing(true);
    try {
      await onProcess(event!.id, notes.trim() || undefined);
      playConfirm();
      onClose();
    } finally {
      setProcessing(false);
    }
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div
          className={cn(
            'w-full max-w-lg bg-background border rounded-2xl shadow-2xl',
            'flex flex-col pointer-events-auto',
            'animate-in fade-in zoom-in-95 duration-150',
          )}
          onClick={(e) => e.stopPropagation()}
        >
          {/* ── Header ─────────────────────────────────── */}
          <div className={cn('px-5 py-4 rounded-t-2xl border-b flex items-start justify-between gap-3', cfg.bg, cfg.border)}>
            <div className="flex items-start gap-3">
              <div className={cn('p-2.5 rounded-xl bg-background/70 border flex-shrink-0', cfg.border)}>
                <SevIcon className={cn('h-5 w-5', cfg.color)} />
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={cn('font-mono font-bold text-base', cfg.color)}>
                    {event.cid_qualifier === 'E' ? '●' : '○'} {event.cid_code}
                  </span>
                  <span className="font-semibold text-foreground text-sm">
                    {getCidDescription(event.cid_code)}
                  </span>
                </div>
                <div className="flex items-center gap-3 mt-0.5 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {fmtTime(event.occurred_at)}
                  </span>
                  <span className={cn('font-semibold', cfg.color)}>
                    ⏱ {fmtElapsed(event.received_at)}
                  </span>
                  {event.zone_no != null ? (
                    <span>
                      Z{event.zone_no + 1}{event.zone_name ? ` — ${event.zone_name}` : ''}
                    </span>
                  ) : event.zone_name ? (
                    <span>Área: {event.zone_name}</span>
                  ) : null}
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-background/80 transition-colors flex-shrink-0"
            >
              <X className="h-4 w-4 text-muted-foreground" />
            </button>
          </div>

          {/* ── Account Info ────────────────────────────── */}
          <div className="px-5 pt-4 pb-2 space-y-4">

            {/* Site card */}
            {loadingSite ? (
              <div className="space-y-2">
                <Skeleton className="h-6 w-2/3" />
                <Skeleton className="h-4 w-1/3" />
                <Skeleton className="h-4 w-1/2" />
              </div>
            ) : event.site_id ? (
              <div className="bg-muted/40 border rounded-xl p-4 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-bold text-foreground text-base leading-tight">
                    {site?.name ?? event.site_name ?? 'Sin nombre'}
                  </h3>
                  <span className={cn('text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0', svcCfg.cls)}>
                    {svcCfg.label}
                  </span>
                </div>
                {site?.account_number && (
                  <div className="flex items-center gap-2 flex-wrap text-xs text-muted-foreground">
                    <div className="flex items-center gap-1.5">
                      <Hash className="h-3 w-3" />
                      <span className="font-mono font-medium">{site.account_number}</span>
                    </div>
                    {accountTier && (
                      <span className={cn(
                        'font-bold px-1.5 py-0.5 rounded-full border text-[10px]',
                        accountTier === 'A' ? 'bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-600' :
                        accountTier === 'B' ? 'bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-600' :
                        'bg-slate-100 text-slate-600 border-slate-300 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-600'
                      )}>
                        Tier {accountTier}
                      </span>
                    )}
                    {accountCreatedAt && (
                      <span className="text-[10px] text-muted-foreground/70">
                        {formatSeniority(accountCreatedAt)}
                      </span>
                    )}
                  </div>
                )}
                {(site?.address ?? site?.city) && (
                  <div className="flex items-start gap-1.5 text-xs text-muted-foreground">
                    <MapPin className="h-3 w-3 mt-0.5 flex-shrink-0" />
                    <span>{[site?.address, site?.city].filter(Boolean).join(', ')}</span>
                  </div>
                )}
                {site?.clave && (
                  <div className="flex items-center gap-1.5 text-xs">
                    <span className="font-semibold text-amber-700 dark:text-amber-400">Palabra Clave:</span>
                    <span className="font-mono font-bold text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-900/40 px-2 py-0.5 rounded">
                      {site.clave}
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-muted/40 border rounded-xl p-4 text-sm text-muted-foreground italic">
                Panel sin cuenta asignada
              </div>
            )}

            {/* Contacts */}
            {allContacts.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Contactos de emergencia
                </p>
                {allContacts.map((c) => (
                  <div
                    key={c.id}
                    className="py-2 px-3 bg-muted/30 rounded-lg hover:bg-muted/60 transition-colors space-y-1"
                  >
                    {/* Name + relation */}
                    <div className="flex items-center gap-2 min-w-0">
                      <User className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                      <span className="text-sm font-medium truncate">{c.name}</span>
                      <span className="text-xs text-muted-foreground flex-shrink-0">
                        {RELATION_LABELS[c.relation] ?? c.relation}
                      </span>
                    </div>
                    {/* Phone numbers */}
                    <div className="flex flex-wrap gap-x-4 gap-y-0.5 pl-5">
                      {c.phone && (
                        <a
                          href={`tel:${c.phone}`}
                          className="flex items-center gap-1 text-xs font-mono text-blue-600 hover:text-blue-700 hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Phone className="h-3 w-3" />
                          {c.phone}
                          <span className="text-muted-foreground font-sans">fijo</span>
                        </a>
                      )}
                      {c.mobile && (
                        <a
                          href={`tel:${c.mobile}`}
                          className="flex items-center gap-1 text-xs font-mono text-blue-600 hover:text-blue-700 hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Phone className="h-3 w-3" />
                          {c.mobile}
                          <span className="text-muted-foreground font-sans">cel</span>
                        </a>
                      )}
                    </div>
                    {/* Notes */}
                    {c.relation_notes && (
                      <p className="text-xs text-muted-foreground pl-5 italic">{c.relation_notes}</p>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Notes */}
            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide block mb-1.5">
                Notas de procesamiento <span className="text-destructive">*</span>
              </label>
              {quickReplies.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {quickReplies.map((r) => (
                    <button
                      key={r.id}
                      type="button"
                      onClick={() => setNotes(r.text)}
                      className={`px-2.5 py-0.5 text-xs rounded-full border transition-colors ${chipCls(r.color)}`}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
              )}
              <textarea
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && notes.trim()) handleProcess(); }}
                placeholder="Ej: Verificado con cliente, código correcto. Sin despacho requerido."
                className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring placeholder:text-muted-foreground"
              />
              <p className="text-xs text-muted-foreground mt-0.5">Obligatorio · Ctrl + Enter para procesar</p>
            </div>
          </div>

          {/* ── Footer ──────────────────────────────────── */}
          <div className="flex gap-3 px-5 pb-5 pt-2">
            <button
              onClick={onClose}
              className="flex-1 py-2.5 border border-input rounded-xl text-sm font-medium hover:bg-muted transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleProcess}
              disabled={processing || !notes.trim()}
              className={cn(
                'flex-1 py-2.5 rounded-xl text-sm font-semibold transition-colors',
                'flex items-center justify-center gap-2 text-white',
                severity === 'CRITICAL' || severity === 'ALARM'
                  ? 'bg-amber-500 hover:bg-amber-600 disabled:opacity-60'
                  : 'bg-primary hover:bg-primary/90 disabled:opacity-60',
              )}
            >
              {processing
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : <CheckCircle className="h-4 w-4" />
              }
              Procesar
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
